
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

export default function MalmesburyPage() {
  useEffect(() => {
    document.title = 'Malmesbury Painters Western Cape | Agricultural Town Painting | Rural Property Services';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional painting services in Malmesbury, Western Cape. Expert agricultural and rural property painting, farm buildings, and residential services. Serving Malmesbury and surrounding farming areas.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Malmesbury painters, agricultural painting, rural property Western Cape');
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/malmesbury`);
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Malmesbury Painters Western Cape",
      "description": "Professional painting services in Malmesbury, Western Cape",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/malmesbury`,
      "areaServed": {
        "@type": "Place",
        "name": "Malmesbury, Western Cape, South Africa"
      },
      "serviceArea": {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": -33.4607,
          "longitude": 18.7267
        },
        "geoRadius": "35000"
      }
    });
    document.head.appendChild(script);

    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Malmesbury Painters')) {
        existingScript.remove();
      }
    };
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 to-black text-white py-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/e06ef62d72ff8af18b21437f92960047.jpg"
            alt="Professional painting services in Malmesbury, Western Cape"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black/70"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Reliable <span className="text-yellow-400">Malmesbury Painters</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Professional painting services for Malmesbury's agricultural community. Expert farm building painting, rural property maintenance, and residential services for the Swartland region.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-yellow-400 text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-yellow-500 transition-colors text-center whitespace-nowrap cursor-pointer"
              >
                Get Quote
              </Link>
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Comprehensive Rural Painting Services in Malmesbury
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized painting solutions for Malmesbury's agricultural and rural properties, from farm buildings to residential homes, designed for the Swartland's unique climate.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-building-3-line',
                title: 'Agricultural Buildings',
                description: 'Professional painting for farm buildings, barns, silos, and agricultural facilities with durable, weather-resistant coatings.',
                features: ['Farm buildings', 'Grain silos', 'Barns & sheds', 'Equipment shelters']
              },
              {
                icon: 'ri-home-4-line',
                title: 'Rural Residential',
                description: 'Complete home painting services for rural properties, farmhouses, and residential homes in the Malmesbury area.',
                features: ['Farmhouses', 'Rural homes', 'Country cottages', 'Estate properties']
              },
              {
                icon: 'ri-store-2-line',
                title: 'Commercial & Industrial',
                description: 'Professional painting for agricultural businesses, processing facilities, and commercial properties in Malmesbury.',
                features: ['Processing facilities', 'Agricultural businesses', 'Industrial buildings', 'Commercial properties']
              }
            ].map((service, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <i className="ri-check-line text-green-500 mr-3"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-black mb-8">
                Why Malmesbury Agricultural Community Chooses Our Services
              </h2>
              <div className="space-y-6">
                {[
                  {
                    title: 'Agricultural Expertise',
                    description: 'Deep understanding of farming operations and agricultural building requirements with appropriate protective coatings.'
                  },
                  {
                    title: 'Rural Community Focus',
                    description: 'Committed to serving the Malmesbury farming community with reliable service and understanding of rural needs.'
                  },
                  {
                    title: 'Weather-Resistant Solutions',
                    description: "Specialized coatings designed for the Swartland's climate conditions including hot summers and wet winters."
                  },
                  {
                    title: 'Flexible Rural Scheduling',
                    description: 'Understanding of farming schedules and seasonal requirements with flexible timing around harvest and planting.'
                  }
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-black text-sm"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-black mb-2">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20Malmesbury%20farm%20building%2C%20agricultural%20painting%20services%2C%20rural%20property%20maintenance%2C%20Swartland%20farming%20area%20Western%20Cape%2C%20farm%20building%20restoration&width=600&height=700&seq=malmesbury-team-work&orientation=portrait"
                alt="Agricultural painting services in Malmesbury"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Recent Malmesbury Agricultural Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcase of our latest agricultural and rural painting projects in Malmesbury, demonstrating our expertise in farm and rural property maintenance.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Wheat Farm Complex',
                location: 'Malmesbury Farming District',
                description: 'Comprehensive painting of a large wheat farm including silos, barns, and processing facilities.',
                image: 'https://readdy.ai/api/search-image?query=Wheat%20farm%20complex%20painting%20in%20Malmesbury%20farming%20district%2C%20agricultural%20silos%20and%20barns%2C%20farm%20building%20painting%2C%20Swartland%20wheat%20farming%20Western%20Cape&width=400&height=300&seq=malmesbury-project-1&orientation=landscape'
              },
              {
                title: 'Historic Farmhouse Restoration',
                location: 'Malmesbury Rural',
                description: 'Careful restoration of a century-old farmhouse maintaining its traditional character.',
                image: 'https://readdy.ai/api/search-image?query=Historic%20farmhouse%20restoration%20painting%20in%20Malmesbury%20rural%20area%2C%20traditional%20country%20home%2C%20heritage%20farm%20property%2C%20rural%20residential%20painting%20Western%20Cape&width=400&height=300&seq=malmesbury-project-2&orientation=landscape'
              },
              {
                title: 'Agricultural Processing Plant',
                location: 'Malmesbury Industrial',
                description: 'Professional painting of a modern agricultural processing and packaging facility.',
                image: 'https://readdy.ai/api/search-image?query=Agricultural%20processing%20plant%20painting%20in%20Malmesbury%20industrial%20area%2C%20modern%20farming%20facility%2C%20agricultural%20business%20painting%2C%20food%20processing%20building%20Western%20Cape&width=400&height=300&seq=malmesbury-project-3&orientation=landscape'
              }
            ].map((project, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <i className="ri-map-pin-line mr-2"></i>
                    {project.location}
                  </div>
                  <h3 className="text-xl font-bold text-black mb-3">{project.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              What Malmesbury Agricultural Community Says
            </h2>
            <p className="text-xl text-gray-600">
              Testimonials from satisfied farmers and rural property owners in Malmesbury
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: 'Pieter van der Merwe',
                location: 'Malmesbury Farming District',
                rating: 5,
                text: 'Excellent work on our wheat farm buildings. They understood our farming schedule and delivered quality results that protect our facilities from the weather.'
              },
              {
                name: 'Susan Botha',
                location: 'Malmesbury Rural',
                rating: 5,
                text: 'Beautiful restoration of our family farmhouse. They respected the heritage character while providing modern protection. Professional service throughout.'
              },
              {
                name: 'Johan Steyn',
                location: 'Malmesbury Industrial',
                rating: 5,
                text: 'Outstanding painting service for our processing facility. They worked around our operations with minimal disruption and delivered excellent results.'
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-600 leading-relaxed mb-6 italic">"{testimonial.text}"</p>
                <div>
                  <div className="font-semibold text-black">{testimonial.name}</div>
                  <div className="text-sm text-gray-5

0">{testimonial.location}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-black mb-6">
            Protect Your Malmesbury Agricultural Property
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact us for specialized agricultural painting consultation and rural property services. Experience reliable painting solutions designed for the Swartland farming community.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
